import React, { useContext, type FC, type ReactNode } from "react"
import { Notification } from "../models/app.interface"
import { NotificationRequests } from "../api/app.service"
import { UserCtx } from "./userContext"
import { useToast } from "../hooks/useToast"

export interface NotificationContext {
  setNotificationAlert: React.Dispatch<
    React.SetStateAction<{
      message: string
      show: boolean
      link: string
      profile_type: "member" | "coach" | null
    }>
  >
  notificationAlert: {
    message: string
    show: boolean
    link: string
    profile_type: "member" | "coach" | null
  }
  hideNotificationAlert: () => void
  lastWebsocketMessage: any
  setLastWebsocketMessage: any
  contactsNotifications: any
  setContactsNotifications: any
  inboxNotifications: any
  setInboxNotifications: any
  notifications: Notification[]
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>
  markRead: (notification_id: number) => void
  markUnread: (notification_id: number) => void
  markAllAsRead: () => void
  getNotifications: () => void
}

export const NotificationCtx = React.createContext<NotificationContext>({
  setNotificationAlert: () => {},
  notificationAlert: { message: "", show: false, link: "", profile_type: null },
  hideNotificationAlert: () => {},
  lastWebsocketMessage: {},
  setLastWebsocketMessage: {},
  contactsNotifications: {},
  setContactsNotifications: {},
  inboxNotifications: {},
  setInboxNotifications: {},
  notifications: [],
  setNotifications: () => {},
  markRead: () => {},
  markUnread: () => {},
  markAllAsRead: () => {},
  getNotifications: () => {},
})

interface Props {
  children: ReactNode
}

export const NotificationCtxProvider: FC<Props> = ({ children }) => {
  const [lastWebsocketMessage, setLastWebsocketMessage] = React.useState<any>(null)
  const [contactsNotifications, setContactsNotifications] = React.useState([])
  const [inboxNotifications, setInboxNotifications] = React.useState([])
  const [notifications, setNotifications] = React.useState<Notification[]>([])
  const [notificationAlert, setNotificationAlert] = React.useState<{
    message: string
    show: boolean
    link: string
    profile_type: "coach" | "member" | null
  }>({
    message: "",
    show: false,
    link: "",
    profile_type: null,
  })

  const { user } = useContext(UserCtx)
  const { toast } = useToast()

  const hideNotificationAlert = () => {
    setNotificationAlert({
      message: "",
      show: false,
      link: "",
      profile_type: null,
    })
  }

  const getNotifications = () => {
    if (user) {
      NotificationRequests.getNotifications({
        profile_id: user.activeProfileId,
      })
        .then((data) => {
          setNotifications(data.notifications)
        })
        .catch((ex) => {
          console.log(ex)
          toast.error(ex.response.data.message)
        })
    }
  }

  const markRead = (notification_id: number) => {
    NotificationRequests.markRead({
      profile_id: user.activeProfileId,
      notification_ids: [notification_id],
    })
      .then(() => {
        // Set as viewed locally so that user sees notification as viewed without refetching data
        const updatedNotifications = notifications.map((n) => {
          if (n.id === notification_id) {
            n.viewed = 1
          }
          return n
        })
        setNotifications(updatedNotifications)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
      })
  }

  const markUnread = (notification_id: number) => {
    NotificationRequests.markUnread({
      profile_id: user.activeProfileId,
      notification_ids: [notification_id],
    })
      .then(() => {
        // Set as viewed locally so that user sees notification as viewed without refetching data
        const updatedNotifications = notifications.map((n) => {
          if (n.id === notification_id) {
            n.viewed = 0
          }
          return n
        })
        setNotifications(updatedNotifications)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
      })
  }

  const markAllAsRead = () => {
    const notificationIds = notifications.map((n) => n.id)
    NotificationRequests.markRead({
      profile_id: user.activeProfileId,
      notification_ids: notificationIds,
    })
      .then(() => {
        // Set as viewed locally so that user sees notification as viewed without refetching data
        const allReadNotifications = notifications.map((n) => {
          n.viewed = 1
          return n
        })
        setNotifications(allReadNotifications)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
      })
  }

  React.useEffect(() => {
    setNotifications((prev) => {
      const newNotifications = [...inboxNotifications, ...prev]
      return newNotifications.filter(
        (notif, index, self) =>
          index === self.findIndex((n) => n.id === notif.id)
      )
    })
  }, [inboxNotifications])

  return (
    <NotificationCtx.Provider
      value={{
        setNotificationAlert,
        hideNotificationAlert,
        notificationAlert,
        lastWebsocketMessage,
        setLastWebsocketMessage,
        contactsNotifications,
        setContactsNotifications,
        inboxNotifications,
        setInboxNotifications,
        notifications,
        setNotifications,
        markRead,
        markUnread,
        markAllAsRead,
        getNotifications,
      }}
    >
      {children}
    </NotificationCtx.Provider>
  )
}
